import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { ConditionSetting } from "./conditionSetting";
// import { CouponCode } from "./couponCode";
import {
  ApplyToLevel,
  ApplyToProductType,
  DiscountType,
  IEditPromotionConditionForm,
  IPromotionConditionDetail,
  IPromotionEventIdsItem,
  UserType,
} from "interfaces";
import { HttpError, useOne, useUpdate } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate } from "react-router-dom";
import Button from "components/button";
import { AdditionalSetup } from "./additionalSetup";
import { useState } from "react";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { endpointService } from "components/shared/services/endpointService";
import { EditPromotionConditionSchema } from "./schema";
import { ViewPromotionCouponCode } from "../../condition/detail";
import { CouponAlreadyExistsModal } from "../../modal";
import { get, isEmpty } from "lodash";
import { canAccessByType } from "utills/helpers";

const INITIAL_DUPLICATE_CODE_MODAL_CONFIG: any = {
  open: false,
  data: null,
};

export const EditPromotionConditionForm = ({
  defaultConditionData,
}: {
  defaultConditionData: IPromotionConditionDetail;
}) => {
  const { mutate, isLoading: isLoading } = useUpdate();
  const navigate = useNavigate();
  const [duplicateCodeModalConfig, setDuplicateCodeModalConfig] = useState(
    INITIAL_DUPLICATE_CODE_MODAL_CONFIG
  );

  const methods = useForm<
    IEditPromotionConditionForm,
    HttpError,
    IEditPromotionConditionForm
  >({
    defaultValues: {
      apply_from: defaultConditionData?.apply_from ?? null,
      apply_from_hour: defaultConditionData?.apply_from_hour ?? null,
      apply_max_item_on_cart_qty: defaultConditionData?.apply_max_item_on_cart_qty,
      apply_product_ids: defaultConditionData?.apply_product_ids ?? [],
      apply_to: defaultConditionData?.apply_to ?? null,
      apply_to_hour: defaultConditionData?.apply_to_hour ?? null,
      apply_to_level: defaultConditionData?.apply_to_level ?? null,
      apply_to_payment_methods:
        defaultConditionData?.apply_to_payment_methods ?? [],
      apply_to_product_type:
        defaultConditionData?.apply_to_product_type ?? null,
      campaign_code: defaultConditionData?.campaign_code ?? null,
      coupon_code: defaultConditionData?.coupon_code ?? null,
      customer_code: defaultConditionData?.customer_code ?? null,
      discount_type: defaultConditionData?.discount_type ?? null,
      discount_type_display:
        defaultConditionData?.discount_type_display ?? null,
      discount_value: defaultConditionData?.discount_value ?? null,
      discount_value_usd: defaultConditionData?.discount_value_usd ?? null,
      event_ids: defaultConditionData?.event_ids ?? [],
      is_active: defaultConditionData?.is_active ?? null,
      max_discount_value: defaultConditionData?.max_discount_value ?? null,
      max_discount_value_usd:
        defaultConditionData?.max_discount_value_usd ?? null,
      max_times_used: defaultConditionData?.max_times_used ?? null,
      max_times_used_per_account:
        defaultConditionData?.max_times_used_per_account ?? null,
      max_times_used_per_day:
        defaultConditionData?.max_times_used_per_day ?? null,
      min_total: defaultConditionData?.min_total ?? null,
      min_total_usd: defaultConditionData?.min_total_usd ?? null,
      note: defaultConditionData?.note ?? null,
      quantity_discount_rules:
        defaultConditionData?.quantity_discount_rules ?? [],
      special_discount: defaultConditionData?.special_discount ?? false,
    },
    mode: "onChange",
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        EditPromotionConditionSchema(
          isShowPaymentMethod,
          isShowUsagePerAccount,
          isShowDailyActiveTime,
          eventCurrencies
        )
      );
      return createResolver(values, context, options);
    },
  });

  const { data: eventData, isFetching: isFetchingEvents } = useOne<
    IPromotionEventIdsItem[],
    HttpError
  >({
    resource: endpointService.getPromotionEndPoint("getPromotionEventDropdown"),
    id: "",
    meta: {
      resource: "data",
    },
    successNotification: (data, values, resource) => {
      const uniqueCurrencies = (data?.data ?? [])
        .map((obj) => obj.currency)
        .filter(
          (currency, index, array) =>
            typeof currency === "string" && array.indexOf(currency) === index
        );
      setEventCurrencies(uniqueCurrencies);
      return {
        message: `Successfully fetched data from ${resource}.`,
        description: "Success with no errors",
        type: "success",
      };
    },
  });

  const [isShowPaymentMethod, setIsShowPaymentMethod] = useState<boolean>(
    (defaultConditionData?.apply_to_payment_methods ?? []).length > 0
      ? true
      : false
  );
  const [isShowUsagePerAccount, setIsShowUsagePerAccount] = useState<boolean>(
    defaultConditionData?.max_times_used_per_account ? true : false
  );
  const [isShowDailyActiveTime, setIsShowDailyActiveTime] = useState<boolean>(
    defaultConditionData?.apply_from_hour ||
      defaultConditionData?.apply_to_hour ||
      defaultConditionData?.max_times_used_per_day
      ? true
      : false
  );

  const [eventCurrencies, setEventCurrencies] = useState<string[]>([]);

  const _onCloseDuplicateCodeModal = () => {
    setDuplicateCodeModalConfig({
      ...INITIAL_DUPLICATE_CODE_MODAL_CONFIG,
      data: duplicateCodeModalConfig.data,
    });
  };

  const onSubmit = (data: IEditPromotionConditionForm) => {
    const submitValues = { ...data };
    console.log(submitValues);
    mutate(
      {
        resource: endpointService.getPromotionEndPoint(
          "updatePromotionCondition",
          {
            conditionId: defaultConditionData?.id,
          }
        ),
        id: "",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/promotion/condition/${defaultConditionData?.id}`);
        },
        onError: (error) => {
          console.log(error);
          const conflictCodes = get(error, "response.data.error.conflicts", []);
          if (!isEmpty(conflictCodes)) {
            setDuplicateCodeModalConfig({
              open: true,
              data: conflictCodes,
            });
          }
        },
      }
    );
  };
  console.log(methods.formState.errors);
  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="w-full h-full">
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          {!isFetchingEvents && !isEmpty(eventData?.data ?? []) && (
            <ConditionSetting
              key={(eventCurrencies ?? []).toString()}
              eventCurrencies={eventCurrencies}
              setEventCurrencies={setEventCurrencies}
              defaultConditionData={defaultConditionData}
              isFetchingEvents={isFetchingEvents}
              eventIds={eventData?.data ?? []}
              setDuplicateCodeModalConfig={setDuplicateCodeModalConfig}
            />
          )}
          <ViewPromotionCouponCode conditionDetailData={defaultConditionData} />
          {canAccessByType(UserType.ADMIN) && (
            <AdditionalSetup
              isShowPaymentMethod={isShowPaymentMethod}
              isShowUsagePerAccount={isShowUsagePerAccount}
              isShowDailyActiveTime={isShowDailyActiveTime}
              setIsShowPaymentMethod={setIsShowPaymentMethod}
              setIsShowUsagePerAccount={setIsShowUsagePerAccount}
              setIsShowDailyActiveTime={setIsShowDailyActiveTime}
            />
          )}
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link to={`/promotion/condition/${defaultConditionData?.id}`}>
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button className="w-20" type="submit">
                Save
              </Button>
            </div>
          </div>
          {(isLoading || isFetchingEvents) ? <FullScreenLoading /> : <></>}
        </div>
      </form>
      <CouponAlreadyExistsModal
        open={duplicateCodeModalConfig.open}
        couponData={duplicateCodeModalConfig.data}
        events={eventData?.data ?? []}
        onClose={_onCloseDuplicateCodeModal}
      />
    </FormProvider>
  );
};
